import React, { useMemo, useReducer, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import I18n from '../../../config/i18n';
import ApiClient from '../../../utils/ApiClient';
import Form from '../../../utils/forms/Form';
import TextInput from '../../../utils/forms/TextInput';
import DateInput from '../../../utils/forms/DateInput';
import TextArea from '../../../utils/forms/TextArea';

import './AvailabilityForm.css';

interface Errors {
  startDate: boolean;
  endDate: boolean;
  name: boolean;
  phone: boolean;
  mail: boolean;
  message: boolean;
}

function errorReducer(
  state: Errors,
  action: { type: 'validate'; field: keyof Errors; hasError: boolean },
): Errors {
  return { ...state, [action.field]: action.hasError };
}

export interface AvailabilityFormProps {
  minDate: moment.Moment;
  accommodationId: number;
  onSuccess: () => void;
  onFail: () => void;
}

export default function AvailabilityForm({
  minDate,
  accommodationId,
  onSuccess,
  onFail,
}: AvailabilityFormProps): JSX.Element {
  const [errors, errorsDispatch] = useReducer(errorReducer, {
    startDate: true,
    endDate: true,
    name: false,
    phone: false,
    mail: true,
    message: true,
  });
  const [sending, setSending] = useState(false);
  const [startDate, setStartDate] = useState<moment.Moment | undefined>(undefined);
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(undefined);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [mail, setMail] = useState('');
  const [message, setMessage] = useState('');

  const disabled = useMemo(() => _.some(_.values(errors)), [errors]);

  if (
    (startDate && endDate == null) ||
    startDate?.isSameOrAfter(moment(endDate).subtract(1, 'day'))
  ) {
    setEndDate(moment(startDate).add(2, 'days'));
  }

  function handleDateChange(type: 'startDate' | 'endDate', day: moment.Moment): void {
    switch (type) {
      case 'startDate':
        setStartDate(day);
        break;
      case 'endDate':
        setEndDate(day);
        break;
      default:
        break;
    }
  }

  function handleTextChange(
    field: 'name' | 'phone' | 'mail' | 'message',
    e: React.ChangeEvent<HTMLInputElement>,
  ): void {
    switch (field) {
      case 'name':
        setName(e.currentTarget.value);
        break;
      case 'phone':
        setPhone(e.currentTarget.value);
        break;
      case 'mail':
        setMail(e.currentTarget.value);
        break;
      case 'message':
        setMessage(e.currentTarget.value);
        break;
      default:
        break;
    }
  }

  function handleValidation(field: keyof Errors, hasError: boolean): void {
    errorsDispatch({ type: 'validate', field, hasError });
  }

  function sendForm(): void {
    setSending(true);
    const inquiry = {
      accommodation_id: accommodationId,
      arrival: startDate && startDate.format('DD.MM.YYYY'),
      departure: endDate && endDate.format('DD.MM.YYYY'),
      email: mail,
      name,
      phone,
      message,
    };
    ApiClient.postInquiry(I18n.locale, inquiry)
      .then((res: any) => {
        setSending(false);
        if (res.data === true) {
          onSuccess();
        } else {
          onFail();
        }
      })
      .catch(() => {
        setSending(false);
        onFail();
      });
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!disabled && !sending) {
      sendForm();
    }
  }

  return (
    <div className="availability-form">
      <h3 className="availability-form-headline">
        {I18n.t('activerecord.attributes.inquiry_form.inquiry')}
      </h3>
      <p className="availability-form-text">{I18n.t('inquiry_form.text')}</p>
      <Form
        className={classNames('availability-form-content', { sending })}
        submitLabel={I18n.t('inquiry_form.send_button')}
        onSubmit={(e): void => handleSubmit(e)}
        disabled={disabled || sending}
      >
        <DateInput
          id="startdate"
          label={I18n.t('activerecord.attributes.inquiry_form.day_of_arrival')}
          value={startDate}
          onChange={(value: moment.Moment): void => handleDateChange('startDate', value)}
          onValidate={(error: boolean): void => handleValidation('startDate', error)}
          minDate={minDate}
          required
        />
        <DateInput
          id="enddate"
          label={I18n.t('activerecord.attributes.inquiry_form.day_of_departure')}
          value={endDate}
          onChange={(value: moment.Moment): void => handleDateChange('endDate', value)}
          onValidate={(error: boolean): void => handleValidation('endDate', error)}
          minDate={moment(startDate).add(1, 'days')}
          required
        />
        <TextInput
          id="name"
          label={I18n.t('activerecord.attributes.inquiry_form.name')}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleTextChange('name', e)}
          onValidate={(error: boolean): void => handleValidation('name', error)}
        />
        <TextInput
          id="phone"
          type="tel"
          label={I18n.t('activerecord.attributes.inquiry_form.phone')}
          value={phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleTextChange('phone', e)}
          onValidate={(error: boolean): void => handleValidation('phone', error)}
        />
        <TextInput
          id="mail"
          type="email"
          label={I18n.t('activerecord.attributes.inquiry_form.email')}
          value={mail}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleTextChange('mail', e)}
          onValidate={(error: boolean): void => handleValidation('mail', error)}
          required
        />
        <TextArea
          id="message"
          label={I18n.t('activerecord.attributes.inquiry_form.message')}
          value={message}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            handleTextChange('message', e)
          }
          onValidate={(error: boolean): void => handleValidation('message', error)}
          required
        />
      </Form>
    </div>
  );
}
